import React from 'react';
import {Link} from 'react-router-dom';
import {AlertTriangleIcon} from '../Icons';

interface AuthCardProps {
    title: string;
    subtitle: string;
    /** Rendered above the form. Null / empty hides the banner entirely. */
    error?: string | null;
    children: React.ReactNode;
    /** Small print under the card — usually a "back to sign in" link. */
    footer?: React.ReactNode;
}

/**
 * The signed-out screen shell: logo, one panel, one heading, one error banner. Extracted so the
 * reset screens cannot drift from the sign-in screen they sit next to in the flow.
 *
 * Control styling lives in `components/formStyles.tsx`, shared with the account screen.
 */
const AuthCard: React.FC<AuthCardProps> = ({title, subtitle, error, children, footer}) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-950 p-4">
    <div className="w-full max-w-md">
      <Link to="/" className="mb-6 flex items-center justify-center gap-2.5">
        <img src="/censorme.svg" alt="" className="h-8 w-8" />
        <span className="text-lg font-semibold text-gray-100">CensorMe</span>
      </Link>

      <div className="rounded-xl border border-gray-800 bg-gray-900 p-6">
        <h1 className="text-base font-semibold text-gray-100">{title}</h1>
        <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/30
           bg-red-500/[0.06] px-3 py-2.5 text-sm text-red-200">
            <AlertTriangleIcon className="mt-0.5 w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {children}
      </div>

      {footer && <div className="mt-4 text-center">{footer}</div>}
    </div>
  </div>
);

export default AuthCard;
